import { CloseIcon } from "@chakra-ui/icons"
import { Box, Button, Checkbox, Flex, HStack, Spinner, Textarea } from "@chakra-ui/react"
import type { QuestionInput } from "@esgt/types"
import { MethodI18nText } from "components/I18nText"
import { useDataSource } from "lib/hooks/useDataSource"
import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { ShortTextInput } from "./ShortTextInput"
import { createRegexFromValidationPattern } from "./createRegexFromValidationPattern"

interface KeyedInputGroupRowsProps {
  textInputs: QuestionInput[]
  isReadOnly: boolean
  saveChanges: () => void
}

/**
 * Renders repeatable groups of inputs (e.g. country select + employee count).
 *
 * The `textInputs` array on the question acts as a template for one group.
 * Form field names stay flat: input_0, input_1 (group 0), input_2, input_3 (group 1), …
 *
 * The user can add/remove as many groups as needed.
 * Removing a group shifts all subsequent groups' values down so there are no
 * gaps, then saves.
 */
export const KeyedInputGroupRows: React.FC<KeyedInputGroupRowsProps> = ({ textInputs, isReadOnly, saveChanges }) => {
  const inputsPerGroup = textInputs.length

  const form = useFormContext()
  const values = form.watch()

  /**
   * Determine how many groups are currently "active" by scanning form values.
   * A group counts as active if any input in it has a non-empty value.
   * We always show at least 1 group.
   */
  const deriveVisibleCount = useCallback((): number => {
    const maxIndex = Object.keys(values)
      .filter((k) => k.startsWith("input_"))
      .reduce((max, k) => Math.max(max, Number.parseInt(k.split("_")[1], 10) + 1), 0)
    const groupCount = Math.ceil(maxIndex / inputsPerGroup)

    let lastPopulated = -1
    for (let g = 0; g < groupCount; g++) {
      for (let j = 0; j < inputsPerGroup; j++) {
        const v = values[`input_${g * inputsPerGroup + j}`]
        if (v !== undefined && v !== "" && v !== null) {
          lastPopulated = g
        }
      }
    }
    return Math.max(1, lastPopulated + 1)
  }, [values, inputsPerGroup])

  const [visibleCount, setVisibleCount] = useState(() => deriveVisibleCount())

  // Keep visibleCount in sync when form values change externally (e.g. reset)
  useEffect(() => {
    const derived = deriveVisibleCount()
    if (derived > visibleCount) {
      setVisibleCount(derived)
    }
  }, [deriveVisibleCount, visibleCount])

  const canRemove = visibleCount > 1

  const handleAdd = () => {
    setVisibleCount((c) => c + 1)
  }

  const handleRemove = (groupIndex: number) => {
    if (!canRemove) return

    // Shift all groups after the removed one down by one position
    for (let g = groupIndex; g < visibleCount - 1; g++) {
      for (let j = 0; j < inputsPerGroup; j++) {
        const srcName = `input_${(g + 1) * inputsPerGroup + j}`
        const dstName = `input_${g * inputsPerGroup + j}`
        form.setValue(dstName, values[srcName] ?? "")
      }
    }

    // Clear the last (now-unused) group
    const lastGroup = visibleCount - 1
    for (let j = 0; j < inputsPerGroup; j++) {
      form.setValue(`input_${lastGroup * inputsPerGroup + j}`, "")
    }

    setVisibleCount((c) => c - 1)
    saveChanges()
  }

  const groupIndices = useMemo(() => Array.from({ length: visibleCount }, (_, i) => i), [visibleCount])

  return (
    <Flex flexDirection="column">
      {/* Column headers — shown once above all rows */}
      <HStack spacing={2} align="flex-end">
        {textInputs.map((input, localIdx) => (
          <Box
            key={`header_${localIdx}`}
            maxWidth={input.size === "sm" ? "230px" : "400px"}
            flex={input.size === "sm" ? "0 0 230px" : "0 0 400px"}
            mt={8}
          >
            {input.text && <MethodI18nText text={input.text} />}
          </Box>
        ))}
      </HStack>

      {groupIndices.map((groupIndex) => (
        <GroupRow
          key={`group_${groupIndex}`}
          textInputs={textInputs}
          groupIndex={groupIndex}
          inputsPerGroup={inputsPerGroup}
          isReadOnly={isReadOnly}
          canRemove={canRemove}
          saveChanges={saveChanges}
          onRemove={handleRemove}
        />
      ))}
      <Box>
        <Button variant="link" mt={1} onClick={handleAdd} isDisabled={isReadOnly}>
          Legg til
        </Button>
      </Box>
    </Flex>
  )
}

// Per-group row component
// Extracted so that each group can independently call useDataSource hooks
// (hooks must be called at the top level of a component, not inside a loop).

interface GroupRowProps {
  textInputs: QuestionInput[]
  groupIndex: number
  inputsPerGroup: number
  isReadOnly: boolean
  canRemove: boolean
  saveChanges: () => void
  onRemove: (groupIndex: number) => void
}

const GroupRow: React.FC<GroupRowProps> = ({
  textInputs,
  groupIndex,
  inputsPerGroup,
  isReadOnly,
  canRemove,
  saveChanges,
  onRemove,
}) => {
  const form = useFormContext()
  const values = form.watch()

  // Find all inputs with a dataSource config and collect their loading states.
  // Because the set of textInputs is fixed for the question, the number of
  // hook calls is stable across renders (satisfying the Rules of Hooks).
  const dataSourceStates = textInputs.map((textInput, localIdx) => {
    const inputName = `input_${groupIndex * inputsPerGroup + localIdx}`
    const triggerValue = textInput.dataSource ? String(values[inputName] ?? "") : ""

    // eslint-disable-next-line react-hooks/rules-of-hooks -- textInputs array is static per question
    return useDataSource({
      dataSource: textInput.dataSource,
      triggerValue,
      setValue: form.setValue,
      inputsPerGroup,
      groupIndex,
      saveChanges,
    })
  })

  // A map from local input index → whether a data source is currently loading
  // values that target that input (for showing a spinner on the target fields).
  const loadingTargets = useMemo(() => {
    const targets = new Set<number>()
    textInputs.forEach((textInput, localIdx) => {
      if (textInput.dataSource && dataSourceStates[localIdx].isLoading) {
        for (const mapping of textInput.dataSource.mappings) {
          targets.add(mapping.targetInput)
        }
      }
    })
    return targets
  }, [textInputs, dataSourceStates])

  return (
    <HStack gap={2}>
      {textInputs.map((textInput, localIdx) => {
        const inputName = `input_${groupIndex * inputsPerGroup + localIdx}`
        const isTargetLoading = loadingTargets.has(localIdx)

        return (
          <Box
            key={inputName}
            maxWidth={textInput.size === "sm" ? "230px" : "400px"}
            flex={textInput.size === "sm" ? "0 0 230px" : "0 0 400px"}
            position="relative"
          >
            {textInput.size === "lg" && (
              <Textarea
                isDisabled={isReadOnly}
                bgColor="white"
                {...form.register(inputName, {
                  pattern: createRegexFromValidationPattern(textInput.validationPattern),
                })}
                onBlur={saveChanges}
              />
            )}
            {["sm", "md"].includes(textInput.size) && (
              <ShortTextInput
                questionInput={textInput}
                disabled={isReadOnly}
                inputName={inputName}
                saveChanges={saveChanges}
              />
            )}
            {isTargetLoading && (
              <Spinner
                size="xs"
                position="absolute"
                right="2"
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
              />
            )}
            {textInput.optional && (
              <Checkbox
                isChecked={values[inputName] === null}
                onChange={() => {
                  form.setValue(inputName, values[inputName] === null ? "" : null)
                  saveChanges()
                }}
              >
                Mangel på tall
              </Checkbox>
            )}
          </Box>
        )
      })}
      <Button variant="ghost" mt={0} isDisabled={isReadOnly || !canRemove} onClick={() => onRemove(groupIndex)}>
        <CloseIcon />
      </Button>
    </HStack>
  )
}
